import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Tooltip,
  Paper,
  Stack,
  Grid,
  Container,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Zoom,
} from "@mui/material";
import {
  RestartAlt as ResetIcon,
  ChevronRight as ArrowIcon,
  TouchApp as TouchIcon,
} from "@mui/icons-material";
import { primaryBtn } from "../../style/AppointmentStyle";
import { jointAssessment } from "../../connections/postApi";
import {
  getJointAssessment,
  getJointAssessmentList,
} from "../../connections/getApi";
import { updateJointAssessment } from "../../connections/putApi";
import { showToast } from "../../utils/toastUtil";

const theme = createTheme({
  palette: {
    primary: { main: "#2563eb", light: "#60a5fa", dark: "#1d4ed8" },
    secondary: { main: "#8b5cf6" },
    background: { default: "#e2e8f0" },
    error: { main: "#ef4444" },
    success: { main: "#10b981" },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Inter", "Roboto", sans-serif',
    h5: { fontWeight: 800, letterSpacing: "-0.5px" },
    h6: { fontWeight: 700 },
  },
  shape: { borderRadius: 20 },
});

// ── MAIN JOINTS ──
const mainJoints = [
  { id: "shoulder_r", label: "Right Shoulder", x: 182, y: 148, type: "lg" },
  { id: "shoulder_l", label: "Left Shoulder", x: 358, y: 148, type: "lg" },
  { id: "elbow_r", label: "Right Elbow", x: 143, y: 290, type: "md" },
  { id: "elbow_l", label: "Left Elbow", x: 397, y: 290, type: "md" },
  { id: "wrist_r", label: "Right Wrist", x: 133, y: 378, type: "sm" },
  { id: "wrist_l", label: "Left Wrist", x: 407, y: 378, type: "sm" },
  { id: "hip_r", label: "Right Hip", x: 210, y: 412, type: "md" },
  { id: "hip_l", label: "Left Hip", x: 330, y: 412, type: "md" },
  { id: "knee_r", label: "Right Knee", x: 213, y: 550, type: "lg" },
  { id: "knee_l", label: "Left Knee", x: 327, y: 550, type: "lg" },
  { id: "ankle_r", label: "Right Ankle", x: 210, y: 662, type: "md" },
  { id: "ankle_l", label: "Left Ankle", x: 330, y: 662, type: "md" },
];

// ── RIGHT HAND DATA (spread fingers) ──
const rightHandData = [
  {
    f: "thumb",
    points: [
      { y: 392, x: 170, id: "cmc", lbl: "CMC" },
      { y: 420, x: 175, id: "mcp", lbl: "MCP" },
      { y: 450, x: 178, id: "ip", lbl: "IP" },
    ],
  },
  {
    f: "index",
    points: [
      { y: 393, x: 153, id: "mcp", lbl: "MCP" },
      { y: 422, x: 150, id: "pip", lbl: "PIP" },
      { y: 452, x: 148, id: "dip", lbl: "DIP" },
    ],
  },
  {
    f: "middle",
    points: [
      { y: 393, x: 133, id: "mcp", lbl: "MCP" },
      { y: 422, x: 131, id: "pip", lbl: "PIP" },
      { y: 452, x: 129, id: "dip", lbl: "DIP" },
    ],
  },
  {
    f: "ring",
    points: [
      { y: 393, x: 113, id: "mcp", lbl: "MCP" },
      { y: 422, x: 112, id: "pip", lbl: "PIP" },
      { y: 452, x: 111, id: "dip", lbl: "DIP" },
    ],
  },
  {
    f: "pinky",
    points: [
      { y: 390, x: 94, id: "mcp", lbl: "MCP" },
      { y: 416, x: 92, id: "pip", lbl: "PIP" },
      { y: 440, x: 91, id: "dip", lbl: "DIP" },
    ],
  },
];

// ── RIGHT FOOT DATA (spread toes, shifted left so both feet have gap) ──
const rightFootData = [
  {
    t: "1",
    points: [
      { y: 690, x: 244, id: "mtp", lbl: "MTP" },
      { y: 714, x: 246, id: "ip", lbl: "IP" },
    ],
  },
  {
    t: "2",
    points: [
      { y: 692, x: 228, id: "mtp", lbl: "MTP" },
      { y: 716, x: 228, id: "pip", lbl: "PIP" },
    ],
  },
  {
    t: "3",
    points: [
      { y: 692, x: 210, id: "mtp", lbl: "MTP" },
      { y: 716, x: 210, id: "pip", lbl: "PIP" },
    ],
  },
  {
    t: "4",
    points: [
      { y: 692, x: 192, id: "mtp", lbl: "MTP" },
      { y: 716, x: 192, id: "pip", lbl: "PIP" },
    ],
  },
  {
    t: "5",
    points: [
      { y: 688, x: 174, id: "mtp", lbl: "MTP" },
      { y: 710, x: 173, id: "pip", lbl: "PIP" },
    ],
  },
];

const mirrorX = (x) => 540 - x;

const generateDetailedJoints = () => {
  const joints = [...mainJoints];

  // ── SKELETON CONNECTION PATHS ──
  // Arms
  const paths = [
    "M 182 148 L 143 290 L 133 378", // right arm
    "M 358 148 L 397 290 L 407 378", // left arm

    // Shoulders connector
    "M 182 148 L 358 148",

    // Spine / neck
    "M 270 99 L 270 148",
    "M 270 148 L 270 412",

    // Torso to hips
    "M 182 148 L 210 412", // right torso→hip
    "M 358 148 L 330 412", // left torso→hip

    // Hip connector
    "M 210 412 L 330 412",

    // RIGHT LEG — hip→knee→ankle  (matches x:210→213→210, y:412→550→662)
    "M 210 412 L 213 550 L 210 662",

    // LEFT LEG — hip→knee→ankle  (matches x:330→327→330, y:412→550→662)
    "M 330 412 L 327 550 L 330 662",
  ];

  const addExtremity = (data, sidePrefix, isFoot) => {
    data.forEach((digit) => {
      let pathString = "";
      digit.points.forEach((pt, idx) => {
        const actualX = sidePrefix === "R" ? pt.x : mirrorX(pt.x);
        joints.push({
          id: `${pt.id}_${sidePrefix}_${digit.f || digit.t}`,
          label: `${sidePrefix} ${digit.lbl || ""} ${digit.f || `Toe ${digit.t}`}`,
          x: actualX,
          y: pt.y,
          type: "xs",
        });
        pathString += `${idx === 0 ? "M" : "L"} ${actualX} ${pt.y} `;
      });

      // Base anchor: right foot = ankle_r x=210, left foot = ankle_l x=330
      // Right wrist = 133, left wrist = 407
      const baseX =
        sidePrefix === "R" ? (isFoot ? 210 : 133) : isFoot ? 330 : 407;
      const baseY = isFoot ? 662 : 378;

      const actualFirstX =
        sidePrefix === "R" ? digit.points[0].x : mirrorX(digit.points[0].x);

      paths.push(`M ${baseX} ${baseY} L ${actualFirstX} ${digit.points[0].y}`);
      paths.push(pathString.trim());
    });
  };

  addExtremity(rightHandData, "R", false);
  addExtremity(rightHandData, "L", false);
  addExtremity(rightFootData, "R", true);
  addExtremity(rightFootData, "L", true);

  return { joints, paths };
};

const { joints: allJoints, paths: connectionPaths } = generateDetailedJoints();

// ── HUMAN BODY SILHOUETTE ──
const HumanBodySilhouette = () => {
  const skin = "#f0d9c2";
  const stroke = "#c4956a";
  const sw = 1.4;
  const detail = "#c4956a";
  const dOp = 0.38;

  return (
    <g>
      {/* HEAD */}
      <circle
        cx="270"
        cy="58"
        r="43"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* ears */}
      <ellipse
        cx="225"
        cy="60"
        rx="7"
        ry="12"
        fill={skin}
        stroke={stroke}
        strokeWidth="1"
      />
      <ellipse
        cx="315"
        cy="60"
        rx="7"
        ry="12"
        fill={skin}
        stroke={stroke}
        strokeWidth="1"
      />

      {/* eyebrows */}
      <path
        d="M 250 47 Q 258 43 265 47"
        fill="none"
        stroke="#6b4c2a"
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity="0.65"
      />
      <path
        d="M 275 47 Q 282 43 290 47"
        fill="none"
        stroke="#6b4c2a"
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity="0.65"
      />

      {/* eyes */}
      <ellipse cx="257" cy="54" rx="5" ry="4" fill="#7c9ab8" opacity="0.75" />
      <ellipse cx="283" cy="54" rx="5" ry="4" fill="#7c9ab8" opacity="0.75" />
      <circle cx="257" cy="54" r="2.2" fill="#1a1a2e" opacity="0.8" />
      <circle cx="283" cy="54" r="2.2" fill="#1a1a2e" opacity="0.8" />
      <circle cx="258" cy="53" r="0.9" fill="white" opacity="0.9" />
      <circle cx="284" cy="53" r="0.9" fill="white" opacity="0.9" />

      {/* nose */}
      <path
        d="M 270 60 Q 266 68 262 71 Q 270 74 278 71 Q 274 68 270 60"
        fill={stroke}
        opacity="0.22"
      />

      {/* mouth */}
      <path
        d="M 261 79 Q 270 85 279 79"
        fill="none"
        stroke={stroke}
        strokeWidth="1.3"
        strokeLinecap="round"
        opacity="0.6"
      />

      {/* NECK */}
      <path
        d="M 257 100 Q 257 133 258 137 L 282 137 Q 283 133 283 100 Z"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* TORSO */}
      <path
        d="M 258 135 L 182 145 Q 157 158 153 184 Q 161 222 177 302 Q 187 364 200 416
           Q 236 428 270 430 Q 304 428 340 416 Q 353 364 363 302 Q 379 222 387 184
           Q 383 158 358 145 L 282 135 Z"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* RIGHT UPPER ARM */}
      <path
        d="M 182 145 Q 157 158 153 184 L 135 292 L 153 297 Q 168 190 192 163 Z"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* RIGHT FOREARM */}
      <path
        d="M 135 292 Q 129 338 126 381 L 141 383 Q 148 342 153 297 Z"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* right hand palm */}
      <ellipse
        cx="133"
        cy="396"
        rx="13"
        ry="22"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* LEFT UPPER ARM */}
      <path
        d="M 358 145 Q 383 158 387 184 L 405 292 L 387 297 Q 372 190 348 163 Z"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* LEFT FOREARM */}
      <path
        d="M 405 292 Q 411 338 414 381 L 399 383 Q 392 342 387 297 Z"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* left hand palm */}
      <ellipse
        cx="407"
        cy="396"
        rx="13"
        ry="22"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* RIGHT UPPER LEG */}
      <path
        d="M 200 418 Q 220 426 226 464 Q 230 504 232 552 L 248 552 Q 248 504 244 464 Q 240 426 244 418 Q 222 410 200 418 Z"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* RIGHT LOWER LEG */}
      <path
        d="M 232 552 Q 230 604 228 664 L 244 666 Q 248 604 248 552 Z"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* right kneecap */}
      <ellipse
        cx="238"
        cy="552"
        rx="11"
        ry="8"
        fill={skin}
        stroke={stroke}
        strokeWidth="1"
        opacity="0.75"
      />

      {/* right foot */}
      <path
        d="M 228 662 Q 218 680 206 693 L 258 695 Q 267 689 264 676 L 244 664 Z"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* LEFT UPPER LEG */}
      <path
        d="M 340 418 Q 320 426 314 464 Q 310 504 308 552 L 292 552 Q 292 504 296 464 Q 300 426 296 418 Q 318 410 340 418 Z"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* LEFT LOWER LEG */}
      <path
        d="M 308 552 Q 310 604 312 664 L 296 666 Q 292 604 292 552 Z"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* left kneecap */}
      <ellipse
        cx="302"
        cy="552"
        rx="11"
        ry="8"
        fill={skin}
        stroke={stroke}
        strokeWidth="1"
        opacity="0.75"
      />

      {/* left foot */}
      <path
        d="M 312 662 Q 322 680 334 693 L 282 695 Q 273 689 276 676 L 296 664 Z"
        fill={skin}
        stroke={stroke}
        strokeWidth={sw}
      />

      {/* ── ANATOMICAL DETAIL LINES ── */}
      {/* clavicles */}
      <line
        x1="270"
        y1="148"
        x2="198"
        y2="160"
        stroke={detail}
        strokeWidth="0.9"
        opacity={dOp}
      />
      <line
        x1="270"
        y1="148"
        x2="342"
        y2="160"
        stroke={detail}
        strokeWidth="0.9"
        opacity={dOp}
      />
      {/* sternum */}
      <line
        x1="270"
        y1="146"
        x2="270"
        y2="298"
        stroke={detail}
        strokeWidth="0.7"
        opacity={dOp * 0.7}
        strokeDasharray="3 4"
      />
      {/* ribcage arcs */}
      <path
        d="M 208 170 Q 270 188 332 170"
        fill="none"
        stroke={detail}
        strokeWidth="0.7"
        opacity={dOp * 0.65}
      />
      <path
        d="M 204 192 Q 270 211 336 192"
        fill="none"
        stroke={detail}
        strokeWidth="0.7"
        opacity={dOp * 0.55}
      />
      <path
        d="M 200 214 Q 270 234 340 214"
        fill="none"
        stroke={detail}
        strokeWidth="0.7"
        opacity={dOp * 0.45}
      />
      <path
        d="M 198 236 Q 270 256 342 236"
        fill="none"
        stroke={detail}
        strokeWidth="0.7"
        opacity={dOp * 0.35}
      />
      {/* navel */}
      <circle
        cx="270"
        cy="302"
        r="3"
        fill="none"
        stroke={detail}
        strokeWidth="0.9"
        opacity={dOp}
      />
      {/* pelvis arc */}
      <path
        d="M 212 390 Q 270 402 328 390"
        fill="none"
        stroke={detail}
        strokeWidth="0.9"
        opacity={dOp}
      />
      {/* shin bone lines — updated to match new leg positions */}
      <line
        x1="236"
        y1="556"
        x2="228"
        y2="662"
        stroke={detail}
        strokeWidth="0.7"
        opacity={dOp * 0.55}
      />
      <line
        x1="304"
        y1="556"
        x2="312"
        y2="662"
        stroke={detail}
        strokeWidth="0.7"
        opacity={dOp * 0.55}
      />
    </g>
  );
};

// ── JOINT NODE ──
const JointNode = ({ joint, isActive, state, onClick }) => {
  const colors = useMemo(() => {
    const isPainful = state.pain[joint.id];
    const isSwollen = state.swelling[joint.id];
    if (isPainful && isSwollen)
      return { main: "#a855f7", glow: "rgba(168,85,247,0.6)" };
    if (isPainful) return { main: "#ef4444", glow: "rgba(239,68,68,0.6)" };
    if (isSwollen) return { main: "#3b82f6", glow: "rgba(59,130,246,0.6)" };
    return { main: "#ffffff", glow: "rgba(255,255,255,0.8)" };
  }, [state, joint.id]);

  const radius = { lg: 16, md: 12, sm: 10, xs: 8 }[joint.type] || 10;

  return (
    <Tooltip title={joint.label} arrow placement="top">
      <g onClick={() => onClick(joint.id)} style={{ cursor: "pointer" }}>
        <circle cx={joint.x} cy={joint.y} r={radius + 8} fill="transparent" />
        {isActive && (
          <circle
            cx={joint.x}
            cy={joint.y}
            r={radius + 5}
            fill={colors.glow}
            style={{ filter: "blur(5px)" }}
          />
        )}
        <circle
          cx={joint.x}
          cy={joint.y}
          r={radius}
          fill={colors.main}
          stroke={colors.main === "#ffffff" ? "#94a3b8" : "#ffffff"}
          strokeWidth={isActive ? 2.5 : 1.5}
          style={{ transition: "all 0.2s ease-out" }}
        />
        {colors.main !== "#ffffff" && (
          <circle
            cx={joint.x - radius / 4}
            cy={joint.y - radius / 4}
            r={radius / 3}
            fill="white"
            fillOpacity={0.45}
          />
        )}
      </g>
    </Tooltip>
  );
};

// ── MAIN COMPONENT ──
const JointAssessment = ({ patientId, resourceId, onClose }) => {
  const [state, setState] = useState({ pain: {}, swelling: {} });
  const [activeMode, setActiveMode] = useState("pain");
  const [existingReportId, setExistingReportId] = useState(null); // ← track existing record
  const [loading, setLoading] = useState(false);

  // ── FETCH EXISTING REPORT ON MOUNT ──
  useEffect(() => {
    if (!resourceId) return;

    const fetchExisting = async () => {
      try {
        const response = await getJointAssessmentList(resourceId);

        // ✅ Correct path: payload.report_assessments (plural), take most recent (index 0)
        const existing = response?.payload?.report_assessments?.[0] ?? null;

        if (existing?.id) {
          setExistingReportId(existing.id);

          // Pre-fill pain/swelling state from saved joints array
          const pain = {};
          const swelling = {};
          (existing.value?.joints ?? []).forEach(
            ({ id, pain: p, swelling: s }) => {
              if (p) pain[id] = true;
              if (s) swelling[id] = true;
            },
          );
          setState({ pain, swelling });
        }
      } catch (err) {
        if (err?.status !== 404) {
          console.error("Failed to load existing assessment:", err);
        }
      }
    };

    fetchExisting();
  }, [resourceId]);

  const toggleJoint = (id) =>
    setState((prev) => ({
      ...prev,
      [activeMode]: { ...prev[activeMode], [id]: !prev[activeMode][id] },
    }));

  const totals = {
    pain: Object.values(state.pain).filter(Boolean).length,
    swelling: Object.values(state.swelling).filter(Boolean).length,
  };

  // ── SUBMIT: CREATE or UPDATE ──
  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const painCount = Object.values(state.pain).filter(Boolean).length;
      const swellingCount = Object.values(state.swelling).filter(
        Boolean,
      ).length;

      const affectedJoints = allJoints
        .filter((joint) => state.pain[joint.id] || state.swelling[joint.id])
        .map((joint) => ({
          id: joint.id,
          label: joint.label,
          pain: !!state.pain[joint.id],
          swelling: !!state.swelling[joint.id],
        }));

      const report_assessment = {
        name: "Joint Pain Assessment",
        value: {
          pain: painCount,
          swelling: swellingCount,
          joints: affectedJoints,
        },
        assessment_type: "orthopedic",
        patient_id: patientId,
        resource_id: resourceId,
        resource_type: "Appointment",
      };

      if (existingReportId) {
        // ── PUT ──
        const response = await updateJointAssessment(
          existingReportId,
          report_assessment,
        );
        // console.log("Report updated:", response);
        if (response?.status?.status === "SUCCESS") {
          showToast("Joint Assessment updated successfully", "SUCCESS");
          onClose?.(); // close modal/drawer if provided
        }
      } else {
        // ── POST ──
        const response = await jointAssessment(report_assessment);
        // Save the new ID so a second click does PUT instead of POST
        const newId = response?.report_assessment?.id ?? response?.id;
        if (newId) setExistingReportId(newId);
        // console.log("Report created:", response);
        if (response?.status?.status === "SUCCESS") {
          showToast("Joint Assessment updated successfully", "SUCCESS");
          onClose?.(); // close modal/drawer if provided
        }
      }

      // onClose?.(); // close modal/drawer if provided
    } catch (err) {
      console.error("Failed to save assessment:", err);
      // TODO: show error snackbar
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh" }}>
        <Container maxWidth="xl">
          <Zoom in timeout={600}>
            <Card
              sx={{
                minHeight: "96vh",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.15)",
                borderRadius: 1,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.6)",
                bgcolor: "rgba(255,255,255,0.88)",
                backdropFilter: "blur(20px)",
                ml: -2,
                display: "flex",
              }}
            >
              <Grid container sx={{ flex: 1 }}>
                {/* ── LEFT SIDEBAR ── */}
                <Grid
                  item
                  xs={12}
                  lg={3}
                  sx={{
                    borderRight: { lg: "1px solid rgba(0,0,0,0.08)" },
                    p: 2,
                    bgcolor: "rgba(255,255,255,0.6)",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: { lg: "94vh" },
                  }}
                >
                  <Stack spacing={3} sx={{ flex: 1 }}>
                    {/* Mode toggle */}
                    <Box>
                      <Typography
                        variant="caption"
                        fontWeight="800"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mb: 1,
                          ml: 1,
                          letterSpacing: 1,
                        }}
                      >
                        ACTIVE MODE
                      </Typography>
                      <ToggleButtonGroup
                        value={activeMode}
                        exclusive
                        onChange={(e, v) => v && setActiveMode(v)}
                        fullWidth
                        sx={{
                          bgcolor: "white",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
                          p: 0.4,
                          "& .MuiToggleButton-root": {
                            border: "none",
                            py: 1.5,
                            borderRadius: "8px",
                            fontWeight: 600,
                            fontSize: "10px",
                          },
                        }}
                      >
                        <ToggleButton
                          value="pain"
                          sx={{
                            "&.Mui-selected": {
                              bgcolor: "error.main",
                              color: "white",
                              "&:hover": { bgcolor: "error.dark" },
                            },
                          }}
                        >
                          Pain / Tenderness
                        </ToggleButton>
                        <ToggleButton
                          value="swelling"
                          sx={{
                            "&.Mui-selected": {
                              bgcolor: "primary.main",
                              color: "white",
                              "&:hover": { bgcolor: "primary.dark" },
                            },
                          }}
                        >
                          Swelling / Effusion
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Counter card */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1,
                        borderRadius: "8px",
                        background:
                          "linear-gradient(135deg, #0f778a 0%, #1984b6 100%)",
                        color: "white",
                        position: "relative",
                        overflow: "hidden",
                        boxShadow: "0 10px 15px -3px rgba(59,130,246,0.3)",
                      }}
                    >
                      <Box sx={{ position: "relative", zIndex: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ opacity: 0.9, fontWeight: 600 }}
                        >
                          Total Active Indicators
                        </Typography>
                        <Typography
                          fontWeight="900"
                          sx={{ fontSize: "2rem", lineHeight: 1, mt: 0.5 }}
                        >
                          {totals.pain + totals.swelling}
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                          <Box>
                            <Typography
                              variant="h6"
                              fontWeight="800"
                              sx={{ color: "#fca5a5" }}
                            >
                              {totals.pain}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                              Painful
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              fontWeight="800"
                              sx={{ color: "#93c5fd" }}
                            >
                              {totals.swelling}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                              Swollen
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <TouchIcon
                        sx={{
                          position: "absolute",
                          right: -20,
                          bottom: -20,
                          fontSize: 120,
                          opacity: 0.1,
                          transform: "rotate(-15deg)",
                        }}
                      />
                    </Paper>

                    {/* Legend */}
                    <Paper
                      sx={{
                        py: 1,
                        px: 1,
                        borderRadius: "8px",
                        boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)",
                        bgcolor: "rgba(255,255,255,0.95)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(0,0,0,0.05)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="800"
                        sx={{ letterSpacing: 1 }}
                      >
                        LEGEND
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
                      >
                        {[
                          {
                            color: "error.main",
                            shadow: "rgba(239,68,68,0.4)",
                            label: "Pain",
                          },
                          {
                            color: "primary.main",
                            shadow: "rgba(59,130,246,0.4)",
                            label: "Swelling",
                          },
                          {
                            color: "#a855f7",
                            shadow: "rgba(168,85,247,0.4)",
                            label: "Both",
                          },
                        ].map(({ color, shadow, label }) => (
                          <Box
                            key={label}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 14,
                                height: 14,
                                borderRadius: "50%",
                                bgcolor: color,
                                boxShadow: `0 0 8px ${shadow}`,
                              }}
                            />
                            <Typography variant="caption" fontWeight="600">
                              {label}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>

                    {/* Reset */}
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => setState({ pain: {}, swelling: {} })}
                      startIcon={<ResetIcon />}
                      color="inherit"
                      sx={{
                        borderRadius: 3,
                        py: 1,
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        borderColor: "rgba(0,0,0,0.1)",
                      }}
                    >
                      Reset Assessment
                    </Button>

                    {/* Generate Report */}
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowIcon />}
                      fullWidth
                      sx={{
                        ...primaryBtn,
                        boxShadow: "0 10px 25px -5px rgba(19,107,119,0.4)",
                        background:
                          "linear-gradient(to right, #139acf, #217cb1)",
                        "&:hover": {
                          background:
                            "linear-gradient(to right, #139acf, #217cb1)",
                          boxShadow: "0 15px 30px -5px rgba(19,107,119,0.5)",
                        },
                      }}
                      onClick={handleGenerateReport} // <-- add this
                      disabled={loading}
                    >
                      {/* { && !loading && " (Update)"} */}
                      {existingReportId ? "Update Report " : "Generate Report"}
                    </Button>
                  </Stack>
                </Grid>

                {/* ── RIGHT PANEL — SVG ── */}
                <Grid
                  item
                  xs={12}
                  lg={9}
                  sx={{
                    position: "relative",
                    p: { xs: 2, lg: 3 },
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    bgcolor: "#f8fafc",
                    minHeight: { lg: "94vh" },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      viewBox="0 0 540 780"
                      style={{
                        height: "92vh",
                        width: "auto",
                        maxWidth: "100%",
                        overflow: "visible",
                      }}
                    >
                      <defs>
                        <pattern
                          id="grid"
                          width="40"
                          height="40"
                          patternUnits="userSpaceOnUse"
                        >
                          <path
                            d="M 40 0 L 0 0 0 40"
                            fill="none"
                            stroke="rgba(0,0,0,0.025)"
                            strokeWidth="1"
                          />
                        </pattern>
                        <filter
                          id="bodyShadow"
                          x="-10%"
                          y="-5%"
                          width="120%"
                          height="110%"
                        >
                          <feDropShadow
                            dx="0"
                            dy="3"
                            stdDeviation="6"
                            floodColor="rgba(0,0,0,0.14)"
                          />
                        </filter>
                      </defs>

                      {/* background grid */}
                      <rect width="540" height="780" fill="url(#grid)" />

                      {/* human body silhouette */}
                      <g filter="url(#bodyShadow)">
                        <HumanBodySilhouette />
                      </g>

                      {/* skeletal connection lines */}
                      <g
                        stroke="#94a3b8"
                        strokeWidth="1.8"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity={0.28}
                      >
                        {connectionPaths.map((path, i) => (
                          <path key={i} d={path} />
                        ))}
                        <circle cx="270" cy="58" r="43" />
                      </g>

                      {/* side labels */}
                      <g
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          fill: "#94a3b8",
                          letterSpacing: 1.5,
                        }}
                      >
                        <text x="22" y="38">
                          RIGHT SIDE
                        </text>
                        <text x="444" y="38">
                          LEFT SIDE
                        </text>
                        <text
                          x="42"
                          y="340"
                          style={{ fill: "#cbd5e1", fontSize: 9 }}
                        >
                          HAND &amp; FINGERS
                        </text>
                        <text
                          x="408"
                          y="340"
                          style={{ fill: "#cbd5e1", fontSize: 9 }}
                        >
                          HAND &amp; FINGERS
                        </text>
                        <text
                          x="108"
                          y="630"
                          style={{ fill: "#cbd5e1", fontSize: 9 }}
                        >
                          FOOT &amp; TOES
                        </text>
                        <text
                          x="365"
                          y="630"
                          style={{ fill: "#cbd5e1", fontSize: 9 }}
                        >
                          FOOT &amp; TOES
                        </text>
                      </g>

                      {/* interactive joints */}
                      {allJoints.map((joint) => (
                        <JointNode
                          key={joint.id}
                          joint={joint}
                          state={state}
                          isActive={state[activeMode][joint.id]}
                          onClick={toggleJoint}
                        />
                      ))}
                    </svg>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Zoom>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default JointAssessment;
